import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MonitorPlay } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { safeNext } from "../lib/safeNext";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const dest = safeNext(next);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(dest);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030305] px-6">
      <motion.div
        {...fadeUp}
        className="w-full max-w-md bg-[#0A0A0F] border border-white/10 rounded-sm p-8 md:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <MonitorPlay className="w-6 h-6 text-[#FF2A2A]" strokeWidth={2.5} />
          <span className="font-display text-xl tracking-tighter uppercase">QTV<span className="text-[#FF2A2A]">Network</span></span>
        </Link>
        <h1 className="font-display text-3xl uppercase tracking-tight mb-1">Welcome back</h1>
        <p className="text-sm text-zinc-500 mb-8">Sign in to keep watching.</p>

        {error && (
          <div data-testid="login-error" className="mb-4 text-sm text-[#FF4D4D] bg-[#FF2A2A]/10 border border-[#FF2A2A]/30 px-4 py-2.5 rounded-sm">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-zinc-500">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full bg-[#030305] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF2A2A] transition"
            />
          </div>
          <button
            data-testid="login-submit-button"
            disabled={busy}
            className="w-full bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white py-3 rounded-sm font-semibold transition-colors"
          >
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p className="mt-4 text-sm text-center">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-zinc-500 hover:text-[#FF2A2A] transition-colors">
            Forgot your password?
          </Link>
        </p>

        <p className="mt-6 text-sm text-zinc-500 text-center">
          New to QTV?{" "}
          <Link to={next ? `/register?next=${encodeURIComponent(next)}` : "/register"} data-testid="go-register-link" className="text-white hover:text-[#FF2A2A] transition-colors">
            Create a free account
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
